import { defineStore } from 'pinia';
import { reactive } from 'vue';
import type { Bounds } from '@kitware/vtk.js/types';
import {
  PanoramicConfig,
  type StateFile,
  type ViewConfig,
} from '@/src/io/state-file/schema';
import { onImageDeleted } from '@/src/composables/onImageDeleted';

export const usePanoramicStore = defineStore('panoramic', () => {
  // A trace belongs to the study, so switching layouts does not discard it.
  const configs = reactive<Record<string, PanoramicConfig>>({});
  function getConfig(dataID: string, bounds: Bounds) {
    configs[dataID] ??= {
      points: [],
      axialPosition: (bounds[4] + bounds[5]) / 2,
      position: 0.5,
      thickness: 5,
      verticalHeight: 80,
      projection: 'average',
    };
    configs[dataID].verticalHeight ??= 80;
    return configs[dataID];
  }
  function removeData(dataID: string) {
    delete configs[dataID];
  }
  function serialize(state: StateFile) {
    const dataIDs = new Set(state.manifest.datasets?.map((d) => d.id));
    Object.values(state.manifest.viewByID ?? {}).forEach((view) => {
      // Include the trace even when another layout is selected at save time.
      dataIDs.forEach((dataID) => {
        if (!configs[dataID]) return;
        view.config ??= {};
        view.config[dataID] ??= {};
        view.config[dataID].panoramic = PanoramicConfig.parse(configs[dataID]);
      });
    });
  }
  function deserialize(_viewID: string, config: Record<string, ViewConfig>) {
    Object.entries(config).forEach(([id, value]) => {
      if (value.panoramic) configs[id] = PanoramicConfig.parse(value.panoramic);
    });
  }
  onImageDeleted((ids) => ids.forEach(removeData));
  return { configs, getConfig, removeData, serialize, deserialize };
});
